import { flow, SnapshotIn, toGenerator, types } from 'mobx-state-tree'
import { FetchModel } from '../models/FetchModel'
import {
    OperationFailure,
    OperationInProgress,
    OperationNotStarted,
    OperationStatusModel,
    OperationSuccess,
  } from '../models/common/OperationStatus'
import { MeasurementUnitsService } from '../models/MeasurementUnitsService'
import { MeasurementUnitInstances, MeasurementUnitsEnum, MeasurementUnitsModel } from '../models/CenterSettings/MeasurementUnitModel'
import type { DeploymentSettingsModel } from '../models/CenterSettings/DeploymentSettingsModel'
import { UserAndPermissionsModel } from '../models/Users/UserModel'
import { OversightDeploymentModel } from 'models/OversightDeploymentSettings/OversightDeploymentModel'
import { DEPLOYMENT_NAME } from 'consts/common'

// A Store for settings used by several pages - like center location, center measurement units etc.
export const CommonSettingsStore = FetchModel.named('CommonSettingsStore')
  .props({
    centerId: types.maybe(types.number),
    centerMeasurementUnits: types.maybe(MeasurementUnitsModel),

    // TODO: Rename model OversightDeploymentModel to DeploymentModel when all settings move from gcs to oversight.
    oversightDeploymentSettings: types.maybeNull(OversightDeploymentModel),

    currentUserAndPermissions: types.maybe(UserAndPermissionsModel),

    fetchCommonSettingsStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get deploymentSettingsEndpointUrl(): string {
      return `${self.baseUrl}deployment_settings/`
    },
    get getCentersUrl(): string {
      return `${self.baseUrl}centers/`
    },
    get authenticateUrl(): string {
      return `${self.oversightBaseUrl}user/permissions`
    },
    get getOversightDeploymentUrl(): string {
      return `${self.oversightBaseUrl}deployments/${DEPLOYMENT_NAME}`
    },
  }))
  .actions((self) => ({
    setMeasurementUnits(measurementUnits: MeasurementUnitsEnum): void {
      self.centerMeasurementUnits = MeasurementUnitInstances[measurementUnits]

      // Important: initialize MeasurementUnitsService
      MeasurementUnitsService.setMeasurementUnits(self.centerMeasurementUnits)
    },
  }))
  .actions((self) => ({
    _loadCenters: flow(function* _loadCenters() {
      return yield* toGenerator(
        self.loadObjectOrError<Array<{id: number, name: string}>>(
          self.getCentersUrl,
          {},
          {fetchOperationName: 'fetch centers'} )
      )
    }),
    _loadDeploymentSettings: flow(function* _loadCenterLocation() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof DeploymentSettingsModel>>(
          self.deploymentSettingsEndpointUrl,
          { }, 
          { fetchOperationName: 'Fetch deployment settings' },
        ),
      )
    }),
    _loadUserInfo: flow(function* loadUserInfo() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof UserAndPermissionsModel>>(
          self.authenticateUrl,
          { method: 'GET' },
          { fetchOperationName: 'Authenticate user' },
        ),
      )
    }),
    _loadOversightDeploymentSettings: flow(function* _loadOversightDeploymentSettings() {
      return yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof OversightDeploymentModel>>(
          self.getOversightDeploymentUrl,
          { method: 'GET' },
          { fetchOperationName: 'Fetch oversight deployment settings' },
        ),
      )
    }),
  }))
  .actions((self) => ({
    initializeCommonSettings: flow(function* initializeCommonSettings() {
        if (self.fetchCommonSettingsStatus.isSuccess) {
          // Don't initialize twice
          return
        }

        self.fetchCommonSettingsStatus = OperationInProgress()
  
        const loadCentersResult = yield* toGenerator(self._loadCenters())
        if (loadCentersResult.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadCentersResult.error)
          return
        }
        const center = loadCentersResult.result[0]
        if (center === undefined) {
            self.fetchCommonSettingsStatus = OperationFailure({name: 'ServerDataError', message: 'GCS must have 1 center location'})
            throw new Error('GCS must have 1 center location')
        }

        const loadDeploymentSettingsResult = yield* toGenerator(self._loadDeploymentSettings())
        if (loadDeploymentSettingsResult.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadDeploymentSettingsResult.error)
          return
        }

        const loadCurrentUserAndPermissions = yield* toGenerator(self._loadUserInfo())
        if (loadCurrentUserAndPermissions.error) {
          self.fetchCommonSettingsStatus = OperationFailure(loadCurrentUserAndPermissions.error)
          return
        }

        self.centerId = center.id
        self.setMeasurementUnits(loadDeploymentSettingsResult.result.measurement_units)
        self.currentUserAndPermissions = UserAndPermissionsModel.create(loadCurrentUserAndPermissions.result)

        const oversightDeploymentSettingsResult = yield* toGenerator(self._loadOversightDeploymentSettings())
        if (oversightDeploymentSettingsResult.error) {
          self.fetchCommonSettingsStatus = OperationFailure(oversightDeploymentSettingsResult.error)
          return
        }
        self.oversightDeploymentSettings = OversightDeploymentModel.create(oversightDeploymentSettingsResult.result)

        self.fetchCommonSettingsStatus = OperationSuccess()
    }),
  }))
